import React, { useEffect, useState, useRef } from "react";
import { Box } from "@chakra-ui/react";
import useAiTypeStore from "../../../stores/modules/aiType.ts";
import useOutlineStore from "../../../stores/modules/outline.ts";

interface ConnectorPosition {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

/**
 * 去除 HTML 标签，用于文本比较
 */
const stripHtml = (html: string): string => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
};

/**
 * HighlightConnector - 显示从 AI 卡片到编辑器文本的连接线
 * 当用户点击 Rephrase/Refine 卡片时，会高亮编辑器中对应的文本并显示连接线
 */
export const HighlightConnector: React.FC = () => {
  const { highlightState, clearHighlight } = useAiTypeStore();
  const { currentSectionIndex } = useOutlineStore();
  const [position, setPosition] = useState<ConnectorPosition | null>(null);
  const prevSectionIndexRef = useRef(currentSectionIndex);

  // 切换 section 时清除高亮
  useEffect(() => {
    if (prevSectionIndexRef.current !== currentSectionIndex) {
      clearHighlight();
      prevSectionIndexRef.current = currentSectionIndex;
    }
  }, [currentSectionIndex, clearHighlight]);

  // 点击其他区域时清除高亮 - 只有点击当前高亮卡片才保持，其它任何操作都清除
  useEffect(() => {
    if (!highlightState.nodeId) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // 只有点击当前高亮的卡片才保持高亮，其它任何点击都清除
      const clickedCard = target.closest('[data-highlight-card="true"]');
      if (clickedCard) return;

      // 点击了其他任何区域，清除高亮
      clearHighlight();
    };

    // 使用 setTimeout 延迟添加监听器，避免立即触发
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [highlightState.nodeId, clearHighlight]);

  // 主效果：高亮目标元素并设置连接线
  useEffect(() => {
    console.log('[HighlightConnector] Main effect triggered', {
      nodeId: highlightState.nodeId,
      selectContent: highlightState.selectContent
    });

    if (!highlightState.nodeId || !highlightState.selectContent) {
      console.log('[HighlightConnector] No nodeId or selectContent, clearing');
      setPosition(null);
      return;
    }

    // 初始查找并滚动到元素
    const editorContainer = document.getElementById('editor-main-container');
    if (!editorContainer) {
      console.log('[HighlightConnector] No editor container');
      return;
    }

    const selectContent = stripHtml(highlightState.selectContent);
    const proseMirror = editorContainer.querySelector('.tiptap')
                       || editorContainer.querySelector('.ProseMirror')
                       || editorContainer.querySelector('[contenteditable="true"]');
    const searchContainer = proseMirror || editorContainer;
    const allElements = searchContainer.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6');

    let initialElement: HTMLElement | null = null;
    for (const el of allElements) {
      const elementText = stripHtml(el.textContent || '');
      if (elementText === selectContent) {
        initialElement = el as HTMLElement;
        break;
      }
      if (elementText.includes(selectContent) && !initialElement) {
        initialElement = el as HTMLElement;
      }
    }

    if (initialElement) {
      console.log('[HighlightConnector] Initial element found, scrolling to view');
      // 应用初始高亮
      initialElement.style.transition = 'all 0.3s ease';
      initialElement.style.backgroundColor = '#FFF3CD';
      initialElement.style.outline = '2px solid #FFC107';
      initialElement.style.borderRadius = '4px';
      // 滚动到视图
      initialElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      console.log('[HighlightConnector] Initial element not found');
    }

    // 每次重新查找元素并计算位置 - 因为 ProseMirror 会重建 DOM 节点
    const calculateAndSetPosition = () => {
      const cardElement = document.querySelector('[data-highlight-card="true"]') as HTMLElement;
      if (!cardElement) {
        console.log('[HighlightConnector] No card element found');
        return;
      }

      const cardRect = cardElement.getBoundingClientRect();

      // 每次重新查找目标元素，因为 ProseMirror 会在编辑时重建 DOM
      const editorContainer = document.getElementById('editor-main-container');
      if (!editorContainer) {
        console.log('[HighlightConnector] No editor container');
        return;
      }

      const selectContent = stripHtml(highlightState.selectContent || '');
      if (!selectContent) return;

      // 查找编辑器
      const proseMirror = editorContainer.querySelector('.tiptap')
                         || editorContainer.querySelector('.ProseMirror')
                         || editorContainer.querySelector('[contenteditable="true"]');
      const searchContainer = proseMirror || editorContainer;

      // 重新查找匹配的元素
      const allElements = searchContainer.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6');
      let targetElement: HTMLElement | null = null;

      for (const el of allElements) {
        const elementText = stripHtml(el.textContent || '');
        if (elementText === selectContent) {
          targetElement = el as HTMLElement;
          break;
        }
        if (elementText.includes(selectContent) && !targetElement) {
          targetElement = el as HTMLElement;
        }
      }

      if (!targetElement) {
        console.log('[HighlightConnector] Target element not found in DOM');
        return;
      }

      // 确保元素已连接到 DOM
      if (!targetElement.isConnected) {
        console.log('[HighlightConnector] Target element not connected');
        return;
      }

      const targetRect = targetElement.getBoundingClientRect();

      console.log('[HighlightConnector] calculateAndSetPosition:', {
        targetElement: targetElement.tagName,
        targetText: targetElement.textContent?.substring(0, 30),
        targetRect: { x: targetRect.x, y: targetRect.y, w: targetRect.width, h: targetRect.height },
        cardRect: { x: cardRect.x, y: cardRect.y, w: cardRect.width, h: cardRect.height }
      });

      // 重新应用高亮样式（因为 ProseMirror 可能重建了元素）
      targetElement.style.transition = 'all 0.3s ease';
      targetElement.style.backgroundColor = '#FFF3CD';
      targetElement.style.outline = '2px solid #FFC107';
      targetElement.style.borderRadius = '4px';

      if (targetRect.width === 0 || targetRect.height === 0) {
        console.log('[HighlightConnector] Target has no size');
        return;
      }

      // 目标点: 段落的右侧中间位置
      const endX = targetRect.right + 5;
      const endY = targetRect.top + targetRect.height / 2;

      // 起点: 卡片左侧中点
      const startX = cardRect.left - 5;
      const startY = cardRect.top + cardRect.height / 2;

      console.log('[HighlightConnector] Setting position:', { startX, startY, endX, endY });
      setPosition({ startX, startY, endX, endY });
    };

    // 多次更新位置以确保滚动完成后正确
    const timers = [
      setTimeout(() => { console.log('[HighlightConnector] Timer 100ms'); calculateAndSetPosition(); }, 100),
      setTimeout(() => { console.log('[HighlightConnector] Timer 300ms'); calculateAndSetPosition(); }, 300),
      setTimeout(() => { console.log('[HighlightConnector] Timer 500ms'); calculateAndSetPosition(); }, 500),
    ];

    // 监听滚动和resize - 包括编辑器容器的滚动
    const handleUpdate = () => calculateAndSetPosition();
    const editorContainerForScroll = document.getElementById('editor-main-container');

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    if (editorContainerForScroll) {
      editorContainerForScroll.addEventListener('scroll', handleUpdate);
    }

    return () => {
      console.log('[HighlightConnector] Cleanup');
      timers.forEach(clearTimeout);
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      if (editorContainerForScroll) {
        editorContainerForScroll.removeEventListener('scroll', handleUpdate);
      }

      // 清除高亮 - 需要重新查找当前的元素
      const currentEditorContainer = document.getElementById('editor-main-container');
      if (currentEditorContainer) {
        const proseMirror = currentEditorContainer.querySelector('.tiptap')
                           || currentEditorContainer.querySelector('.ProseMirror')
                           || currentEditorContainer.querySelector('[contenteditable="true"]');
        const searchContainer = proseMirror || currentEditorContainer;
        const selectContent = stripHtml(highlightState.selectContent || '');

        if (selectContent) {
          const allElements = searchContainer.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6');
          for (const el of allElements) {
            const elementText = stripHtml(el.textContent || '');
            if (elementText === selectContent || elementText.includes(selectContent)) {
              const htmlEl = el as HTMLElement;
              htmlEl.style.backgroundColor = '';
              htmlEl.style.outline = '';
              htmlEl.style.borderRadius = '';
              htmlEl.style.transition = '';
              break;
            }
          }
        }
      }
    };
  }, [highlightState.nodeId, highlightState.selectContent]);

  if (!position || !highlightState.nodeId) {
    return null;
  }

  // 箭头尺寸
  const arrowWidth = 12;
  const arrowHeight = 8;

  // 线段终点应该在箭头的右侧中间（箭头尖端在 endX，箭头底部在 endX + arrowWidth）
  const lineEndX = position.endX + arrowWidth;
  const lineEndY = position.endY;

  // 计算 SVG 路径 - 使用水平曲线连接
  const controlPointOffset = Math.min(50, Math.abs(position.startX - lineEndX) / 3);

  const pathD = `M ${lineEndX} ${lineEndY}
                 C ${lineEndX + controlPointOffset} ${lineEndY},
                   ${position.startX - controlPointOffset} ${position.startY},
                   ${position.startX} ${position.startY}`;

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      width="100vw"
      height="100vh"
      pointerEvents="none"
      zIndex={1000}
    >
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        {/* 连接线 - 从箭头右侧中间到卡片左侧 */}
        <path
          d={pathD}
          fill="none"
          stroke="#3898FF"
          strokeWidth={2}
          strokeDasharray="6,4"
          style={{
            animation: 'dash 1s linear infinite',
          }}
        />
        {/* 目标端 - 箭头指向文本（尖端在左，底部在右） */}
        <polygon
          points={`${position.endX},${position.endY} ${position.endX + arrowWidth},${position.endY - arrowHeight / 2} ${position.endX + arrowWidth},${position.endY + arrowHeight / 2}`}
          fill="#3898FF"
        />
        {/* 起点标记 - 卡片端 */}
        <circle
          cx={position.startX}
          cy={position.startY}
          r={5}
          fill="#3898FF"
        />
      </svg>
      <style>
        {`
          @keyframes dash {
            to {
              stroke-dashoffset: -10;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default HighlightConnector;
